import { useEffect, useRef } from "react";
import * as THREE from "three";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { getPartColor } from "./partColors";

export default function StlViewer({ meshUrls, reloadKey, token }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const container = canvasRef.current;
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xffffff);

    const group = new THREE.Group();
    scene.add(group);

    const camera = new THREE.PerspectiveCamera(50, 1, 0.1, 2000);
    // camera.position.set(0, -500, 300);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(container.clientWidth, container.clientHeight);
    renderer.gammaFactor = 2.2;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap; 
    container.appendChild(renderer.domElement);


    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.enablePan = true;
    controls.minDistance = 10;
    controls.maxDistance = 1000;
 

    const light1 = new THREE.DirectionalLight( 0xffffff, 1.5);
    light1.position.set( -100,-100,50);
    light1.castShadow = true;
    scene.add(light1);

    const light2 = new THREE.DirectionalLight( 0xffffff, 1.5);
    light2.position.set( 100,100,50);
    light2.castShadow = true;
    scene.add(light2);

    scene.add(new THREE.AmbientLight(0xcef5ff, 0.2));


    const loader = new STLLoader();

    meshUrls.forEach((url) => {
      const apiBase = process.env.REACT_APP_API_BASE_URL || "";
      const isApiUrl = apiBase && url.startsWith(apiBase);

      if (isApiUrl && token) {
        loader.setRequestHeader({ Authorization: `Bearer ${token}` });
      } else {
        loader.setRequestHeader({});
      }

      const finalUrl = isApiUrl ? (url.includes("?") ? `${url}&t=${Date.now()}` : `${url}?t=${Date.now()}`) : url;

      loader.load(
        finalUrl,
        (geometry) => {
          const material = new THREE.MeshPhysicalMaterial({
            color: getPartColor(url),
            metalness: 0.05,
            roughness: 0.15,
            clearcoat:0.2,
            clearcoatRoughness:0.4,
            thickness:0.5,
            transmission: pickTransmission(url),
            transparent: pickTransmission(url) > 0,
            opacity: pickTransmission(url) > 0 ? 0.8 : 1,
            depthWrite: pickTransmission(url) === 0,
          });
          const mesh = new THREE.Mesh(geometry, material);
          mesh.castShadow = true;
          scene.add(mesh);
          group.add(mesh);

          const box = new THREE.Box3().setFromObject(group);
          const center = box.getCenter(new THREE.Vector3());
          const size = box.getSize(new THREE.Vector3());
          const maxDim = Math.max(size.x, size.y, size.z);
          const fitDistance =
            maxDim / (2 * Math.tan((Math.PI * camera.fov) / 360)) + 50; // little padding

          camera.position.copy(center.clone().add(new THREE.Vector3(0, -fitDistance, 0)));
          controls.target.copy(center);
          controls.update();
        },
        undefined,
        (error) => console.error(`Failed to load ${url}`, error)
      );
    });


    const animate = () => {
      requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    const handleResize = () => {
      const { clientWidth, clientHeight } = container;
      renderer.setSize(clientWidth, clientHeight);
      camera.aspect = clientWidth / clientHeight;
      camera.updateProjectionMatrix();
    };
    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
      controls.dispose();
      renderer.dispose();
      container.removeChild(renderer.domElement);
     
    };
  }, [meshUrls, reloadKey]);

  return <div ref={canvasRef} style={{ width: "100%", height: "100%" }} />;
}

const TRANSMISSION_MAP = {
  disc:0.2,
  cord:0.2,
  vertebra: 0.0,
  sacrum: 0.0,
  hip:0.0,
  femur:0.0,
};

function pickTransmission(url) {
  const key = Object.keys(TRANSMISSION_MAP).find((k) => url.includes(k));
  return key ? TRANSMISSION_MAP[key] : 0.9; // default
}
